// AUS (Automated Underwriting System) service layer
// Handles Desktop Underwriter (DU) and Loan Product Advisor (LPA) execution
// Will later integrate with real Fannie Mae DU and Freddie Mac LPA APIs

/**
 * Runs both DU and LPA in parallel
 * @param {Object} loanData - Current loan data
 * @returns {Promise<Object>} Object containing both DU and LPA results
 */
export const runDualAUS = async (loanData) => {
  // Simulate parallel execution
  const [duResult, lpaResult] = await Promise.all([
    runDesktopUnderwriter(loanData),
    runLoanProductAdvisor(loanData)
  ]);
  
  return {
    du: duResult,
    lpa: lpaResult,
    timestamp: new Date().toISOString(),
    loanSnapshot: { ...loanData }
  };
};

/**
 * Runs Fannie Mae Desktop Underwriter (DU)
 * @param {Object} loanData - Loan data
 * @returns {Promise<Object>} DU findings
 */
const runDesktopUnderwriter = async (loanData) => {
  // Mock implementation - will later call Fannie Mae DU API
  return new Promise((resolve) => {
    setTimeout(() => {
      const result = generateMockDUResponse(loanData);
      resolve(result);
    }, 2000);
  });
};

/**
 * Runs Freddie Mac Loan Product Advisor (LPA)
 * @param {Object} loanData - Loan data
 * @returns {Promise<Object>} LPA findings
 */
const runLoanProductAdvisor = async (loanData) => {
  // Mock implementation - will later call Freddie Mac LPA API
  return new Promise((resolve) => {
    setTimeout(() => {
      const result = generateMockLPAResponse(loanData);
      resolve(result);
    }, 2000);
  });
};

/**
 * Generates mock DU response with realistic findings
 */
const generateMockDUResponse = (loanData) => {
  const creditScore = loanData.borrower?.creditScore || 740;
  const ltv = loanData.financial?.ltv || 80;
  const dti = loanData.financial?.dti || 35;
  
  // Determine overall recommendation
  let recommendation = 'Approve/Eligible';
  let recommendationCode = 'approve';
  
  if (creditScore < 640 || ltv > 95 || dti > 45) {
    recommendation = 'Refer';
    recommendationCode = 'refer';
  } else if (creditScore < 620 || ltv > 97) {
    recommendation = 'Ineligible';
    recommendationCode = 'ineligible';
  }
  
  // Rep & Warranty relief (DU tends to be more lenient with good credit)
  const repWarranty = {
    income: {
      status: creditScore >= 700 && dti <= 43 ? 'granted' : creditScore >= 660 ? 'partial' : 'denied',
      explanation: creditScore >= 700 && dti <= 43 
        ? 'Income verification relief granted based on strong credit profile and acceptable DTI'
        : creditScore >= 660
        ? 'Partial relief - additional income documentation required'
        : 'No relief - full income verification required'
    },
    assets: {
      status: creditScore >= 720 && ltv <= 80 ? 'granted' : creditScore >= 680 ? 'partial' : 'denied',
      explanation: creditScore >= 720 && ltv <= 80
        ? 'Asset verification relief granted based on excellent credit and LTV'
        : creditScore >= 680
        ? 'Partial relief - bank statements required for reserves only'
        : 'No relief - full asset verification required'
    },
    employment: {
      status: creditScore >= 700 ? 'granted' : creditScore >= 640 ? 'partial' : 'denied',
      explanation: creditScore >= 700
        ? 'Employment verification relief granted - verbal VOE acceptable'
        : creditScore >= 640
        ? 'Partial relief - written VOE required but paystubs may be waived'
        : 'No relief - full employment verification required'
    },
    appraisal: {
      status: ltv <= 70 && creditScore >= 740 ? 'granted' : ltv <= 80 ? 'partial' : 'denied',
      explanation: ltv <= 70 && creditScore >= 740
        ? 'Appraisal waiver granted - property inspection waiver (PIW) eligible'
        : ltv <= 80
        ? 'Partial relief - exterior-only inspection may be acceptable'
        : 'No relief - full interior/exterior appraisal required'
    }
  };
  
  // Key messages
  const messages = [];
  if (recommendation === 'Approve/Eligible') {
    messages.push('Loan meets Fannie Mae guidelines');
    if (repWarranty.appraisal.status === 'granted') {
      messages.push('Property Inspection Waiver (PIW) available');
    }
    if (repWarranty.income.status === 'granted') {
      messages.push('Income documentation relief available');
    }
  } else if (recommendation === 'Refer') {
    messages.push('Additional review required');
    if (dti > 43) messages.push('DTI exceeds standard threshold - compensating factors needed');
    if (ltv > 90) messages.push('High LTV - additional reserves may be required');
  } else {
    messages.push('Does not meet Fannie Mae minimum requirements');
    if (creditScore < 620) messages.push('Credit score below minimum');
    if (ltv > 97) messages.push('LTV exceeds maximum');
  }
  
  return {
    system: 'DU',
    systemName: 'Desktop Underwriter',
    casefileId: `DU-${Date.now()}`,
    recommendation,
    recommendationCode,
    messages,
    repWarranty,
    findings: {
      creditScore,
      ltv,
      dti,
      loanPurpose: loanData.loanPurpose || 'Purchase',
      propertyType: loanData.property?.type || 'Single Family',
      occupancy: loanData.property?.occupancy || 'Primary'
    },
    timestamp: new Date().toISOString()
  };
};

/**
 * Generates mock LPA response with realistic findings
 */
const generateMockLPAResponse = (loanData) => {
  const creditScore = loanData.borrower?.creditScore || 740;
  const ltv = loanData.financial?.ltv || 80;
  const dti = loanData.financial?.dti || 35;
  
  // Determine overall recommendation (LPA tends to be slightly more conservative)
  let recommendation = 'Accept';
  let recommendationCode = 'accept';
  
  if (creditScore < 660 || ltv > 95 || dti > 43) {
    recommendation = 'Caution';
    recommendationCode = 'caution';
  } else if (creditScore < 620 || ltv > 97) {
    recommendation = 'Ineligible';
    recommendationCode = 'ineligible';
  }
  
  // Rep & Warranty relief (LPA is more conservative, especially on income)
  const repWarranty = {
    income: {
      status: creditScore >= 720 && dti <= 40 ? 'granted' : creditScore >= 680 ? 'partial' : 'denied',
      explanation: creditScore >= 720 && dti <= 40
        ? 'Income representation and warranty relief granted'
        : creditScore >= 680
        ? 'Partial relief - tax returns required but paystubs may be waived'
        : 'No relief - full income documentation required'
    },
    assets: {
      status: creditScore >= 740 && ltv <= 75 ? 'granted' : creditScore >= 700 ? 'partial' : 'denied',
      explanation: creditScore >= 740 && ltv <= 75
        ? 'Asset verification relief granted'
        : creditScore >= 700
        ? 'Partial relief - most recent statement only required'
        : 'No relief - full asset verification required'
    },
    employment: {
      status: creditScore >= 720 ? 'granted' : creditScore >= 660 ? 'partial' : 'denied',
      explanation: creditScore >= 720
        ? 'Employment verification relief granted'
        : creditScore >= 660
        ? 'Partial relief - verbal VOE acceptable with written confirmation'
        : 'No relief - full employment verification required'
    },
    appraisal: {
      status: ltv <= 65 && creditScore >= 760 ? 'granted' : ltv <= 75 ? 'partial' : 'denied',
      explanation: ltv <= 65 && creditScore >= 760
        ? 'Appraisal waiver granted - Automated Collateral Evaluation (ACE) eligible'
        : ltv <= 75
        ? 'Partial relief - desktop appraisal may be acceptable'
        : 'No relief - full appraisal required'
    }
  };
  
  // Key messages
  const messages = [];
  if (recommendation === 'Accept') {
    messages.push('Loan meets Freddie Mac guidelines');
    if (repWarranty.appraisal.status === 'granted') {
      messages.push('Automated Collateral Evaluation (ACE) available');
    }
    if (repWarranty.income.status === 'granted') {
      messages.push('Income rep & warranty relief available');
    }
  } else if (recommendation === 'Caution') {
    messages.push('Eligible with conditions');
    if (dti > 40) messages.push('DTI above preferred threshold - strong compensating factors required');
    if (ltv > 90) messages.push('High LTV - additional reserves required');
  } else {
    messages.push('Does not meet Freddie Mac minimum requirements');
    if (creditScore < 620) messages.push('Credit score below minimum');
    if (ltv > 97) messages.push('LTV exceeds maximum');
  }
  
  return {
    system: 'LPA',
    systemName: 'Loan Product Advisor',
    casefileId: `LPA-${Date.now()}`,
    recommendation,
    recommendationCode,
    messages,
    repWarranty,
    findings: {
      creditScore,
      ltv,
      dti,
      loanPurpose: loanData.loanPurpose || 'Purchase',
      propertyType: loanData.property?.type || 'Single Family',
      occupancy: loanData.property?.occupancy || 'Primary'
    },
    timestamp: new Date().toISOString()
  };
};

/**
 * Generates AI insights comparing DU and LPA results
 * @param {Object} duResult - DU findings
 * @param {Object} lpaResult - LPA findings
 * @param {Object} loanData - Loan data
 * @returns {Object} AI insights
 */
export const generateAUSInsights = (duResult, lpaResult, loanData) => {
  const insights = {
    summary: '',
    differences: [],
    recommendations: [],
    repWarrantyAnalysis: []
  };
  
  // Overall comparison
  if (duResult.recommendationCode === lpaResult.recommendationCode) {
    insights.summary = `Both DU and LPA returned ${duResult.recommendation}. This indicates strong alignment between Fannie Mae and Freddie Mac guidelines for this loan.`;
  } else {
    insights.summary = `DU returned ${duResult.recommendation} while LPA returned ${lpaResult.recommendation}. This divergence suggests different risk assessments between the agencies.`;
    
    insights.differences.push({
      category: 'Overall Recommendation',
      du: duResult.recommendation,
      lpa: lpaResult.recommendation,
      explanation: 'Freddie Mac (LPA) tends to be more conservative on DTI ratios and requires stronger credit profiles for relief.'
    });
  }
  
  // Rep & Warranty comparison
  const categories = ['income', 'assets', 'employment', 'appraisal'];
  categories.forEach(category => {
    const duStatus = duResult.repWarranty[category].status;
    const lpaStatus = lpaResult.repWarranty[category].status;
    
    if (duStatus !== lpaStatus) {
      const categoryName = category.charAt(0).toUpperCase() + category.slice(1);
      insights.repWarrantyAnalysis.push({
        category: categoryName,
        du: duStatus,
        lpa: lpaStatus,
        explanation: `DU ${duStatus === 'granted' ? 'granted' : duStatus === 'partial' ? 'partially granted' : 'denied'} ${category} relief, while LPA ${lpaStatus === 'granted' ? 'granted' : lpaStatus === 'partial' ? 'partially granted' : 'denied'} it.`,
        reason: getDifferenceReason(category, duStatus, lpaStatus, loanData)
      });
    }
  });
  
  // Recommendations
  const creditScore = loanData.borrower?.creditScore || 740;
  const ltv = loanData.financial?.ltv || 80;
  const dti = loanData.financial?.dti || 35;
  
  if (creditScore < 720) {
    insights.recommendations.push({
      title: 'Improve Credit Score',
      description: `Current score: ${creditScore}. Increasing to 720+ would unlock additional rep & warranty relief in both systems.`,
      impact: 'High - Could grant income and employment relief'
    });
  }
  
  if (ltv > 80) {
    insights.recommendations.push({
      title: 'Reduce LTV',
      description: `Current LTV: ${ltv}%. Reducing to 80% or below would improve appraisal waiver eligibility.`,
      impact: 'Medium - Could grant appraisal relief'
    });
  }
  
  if (dti > 40) {
    insights.recommendations.push({
      title: 'Lower DTI',
      description: `Current DTI: ${dti}%. LPA prefers DTI ≤ 40% for income relief.`,
      impact: 'Medium - Could improve LPA recommendation'
    });
  }
  
  // Which AUS to proceed with
  const duReliefCount = Object.values(duResult.repWarranty).filter(r => r.status === 'granted').length;
  const lpaReliefCount = Object.values(lpaResult.repWarranty).filter(r => r.status === 'granted').length;
  
  if (duReliefCount > lpaReliefCount) {
    insights.recommendations.push({
      title: 'Proceed with DU',
      description: `DU granted ${duReliefCount} rep & warranty reliefs vs LPA's ${lpaReliefCount}. DU offers cleaner execution for this file.`,
      impact: 'High - Fewer documentation requirements'
    });
  } else if (lpaReliefCount > duReliefCount) {
    insights.recommendations.push({
      title: 'Proceed with LPA',
      description: `LPA granted ${lpaReliefCount} rep & warranty reliefs vs DU's ${duReliefCount}. LPA offers cleaner execution for this file.`,
      impact: 'High - Fewer documentation requirements'
    });
  } else {
    insights.recommendations.push({
      title: 'Either AUS Acceptable',
      description: `Both systems granted ${duReliefCount} rep & warranty reliefs. Choose based on investor preference or pricing.`,
      impact: 'Low - Similar execution path'
    });
  }
  
  return insights;
};

/**
 * Helper function to explain why DU and LPA differ on R&W relief
 */
const getDifferenceReason = (category, duStatus, lpaStatus, loanData) => {
  const creditScore = loanData.borrower?.creditScore || 740;
  const ltv = loanData.financial?.ltv || 80;
  const dti = loanData.financial?.dti || 35;
  
  if (category === 'income') {
    if (duStatus === 'granted' && lpaStatus !== 'granted') {
      return `DU accepts DTI up to 43% for income relief, while LPA prefers 40% or below. Current DTI: ${dti}%`;
    }
  } else if (category === 'assets') {
    if (duStatus === 'granted' && lpaStatus !== 'granted') {
      return `DU grants asset relief at 80% LTV with 720+ credit, while LPA requires 75% LTV and 740+ credit. Current: ${ltv}% LTV, ${creditScore} credit`;
    }
  } else if (category === 'appraisal') {
    if (duStatus === 'granted' && lpaStatus !== 'granted') {
      return `DU offers PIW at 70% LTV with 740+ credit, while LPA requires 65% LTV and 760+ credit for ACE. Current: ${ltv}% LTV, ${creditScore} credit`;
    }
  }
  
  return 'Different risk tolerance and relief thresholds between Fannie Mae and Freddie Mac';
};

/**
 * Saves AUS selection
 * @param {number} loanId - Loan ID
 * @param {string} selectedAUS - 'DU' or 'LPA'
 * @param {Object} ausResults - Full AUS results
 * @returns {Promise<Object>} Saved selection
 */
export const saveAUSSelection = async (loanId, selectedAUS, ausResults) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const selection = {
        loanId,
        selectedAUS,
        selectedAt: new Date().toISOString(),
        casefileId: selectedAUS === 'DU' ? ausResults.du.casefileId : ausResults.lpa.casefileId,
        recommendation: selectedAUS === 'DU' ? ausResults.du.recommendation : ausResults.lpa.recommendation
      };
      resolve(selection);
    }, 300);
  });
};

// Export constants
export const AUS_SYSTEMS = {
  DU: 'DU',
  LPA: 'LPA'
};

export const REP_WARRANTY_STATUS = {
  GRANTED: 'granted',
  PARTIAL: 'partial',
  DENIED: 'denied'
};
